import Image from 'next/image';

import { APP_ENVS } from '@/shared/constants/envs';

import { cn } from '@/lib/utils';

/**
 * Competitor icons live at `public/images/compare/<slug>.png`: 128px favicons
 * fetched from Google's favicon service (a few vendors only serve 48px). They
 * are rendered at 24–48px, so the source size is never the limit.
 */
export const getCompetitorLogoSrc = (slug: string) => `/images/compare/${slug}.png`;

const BINARY_LOGO_SRC = '/brand/binary_logo_short.png';

type LogoSize = 'sm' | 'md' | 'lg';

const SIZE_CLASS: Record<LogoSize, string> = {
	sm: 'size-6 rounded-md',
	md: 'size-8 rounded-lg',
	lg: 'size-12 rounded-xl md:size-14',
};

const SIZE_PX: Record<LogoSize, number> = { sm: 24, md: 32, lg: 56 };

type LogoProps = {
	size?: LogoSize;
	className?: string;
};

type CompetitorLogoProps = LogoProps & {
	/** Comparison page slug, e.g. "binary-vs-ashby". */
	slug: string;
	/** Competitor name, used for alt text. */
	name: string;
};

/** Square favicon chip for a competitor, with a hairline border so light icons stay visible. */
export const CompetitorLogo = ({ slug, name, size = 'md', className }: CompetitorLogoProps) => (
	<Image
		src={getCompetitorLogoSrc(slug)}
		alt={`${name} logo`}
		width={SIZE_PX[size]}
		height={SIZE_PX[size]}
		className={cn('shrink-0 border border-border bg-background object-cover', SIZE_CLASS[size], className)}
	/>
);

/** Binary's own mark, sized to sit next to a `CompetitorLogo`. */
export const BinaryLogo = ({ size = 'md', className }: LogoProps) => (
	<Image
		src={BINARY_LOGO_SRC}
		alt={`${APP_ENVS.COMPANY_NAME} logo`}
		width={SIZE_PX[size]}
		height={SIZE_PX[size]}
		className={cn('shrink-0 object-cover', SIZE_CLASS[size], className)}
	/>
);
